"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp peak leisure surges against prolonged monsoon and shoulder dips. We build dynamic minimum length-of-stay controls and pace-based pricing so your property never relies on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pacing & minimum stay restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com. Automated competitive alerts and yield adjustments protect your RevPAR while securing high-intent bookings ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commissions. We enforce strict rate parity across meta-search and drive direct guest conversions through targeted booking incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapegoa" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Goa
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Goa presents a unique micro-market with compressed booking windows, volatile seasonal swings, and intense OTA competition. Sunil Singh structures disciplined revenue models designed specifically for coastal and boutique resorts.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 rounded-xl">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Goa hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}